// VideoFeed.js
import React, { useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import usePlaceStreams from '../../hooks/usePlaceStreams';
import { PLACE_STREAMS_LIMIT, resultsOf, sortByScope } from '../../utils/streams';
import { LoadMore } from '../feed/FeedCard';
import StreamCard from './StreamCard';
import StreamPlayerModal from './StreamPlayerModal';

import './Streams.css';

/**
 * VideoFeed
 *
 * The Live streams view of the US home page, at /us/live-streams: every stream for the reader's place --
 * their city, county, state and federal government -- as feed items, in three
 * sections:
 *
 *   Live now     streaming this minute, local first; continuous cameras last
 *   Scheduled    the next 72 hours, soonest first, in the reader's own time
 *   Recordings   streams that ended in the last 24 hours
 *
 * Each card opens the in-app player. Refreshed once a minute (usePlaceStreams).
 *
 * Each section shows SECTION_STEP streams at first and a Show more button for
 * the next SECTION_STEP, centered under the section. /streams/place does not
 * page, so every stream a section can show arrives in the one request
 * (PLACE_STREAMS_LIMIT per section) and Show more only reveals what is already
 * loaded -- no request, no wait. How far each section is opened survives the
 * minute refresh. A section that came back holding the full limit may have
 * had more cut off by the API, and says so once it is fully open.
 *
 * Two bubbles at the top, each shown only when it is true for this reader:
 * their city's official site links no YouTube channel, or their county's does
 * not. See `noneIn` below for how that is decided. Nothing else the API says
 * is shown.
 */
const SECTION_STEP = 10;

function VideoFeed() {
  const { t } = useTranslation('common');
  const { data, loading } = usePlaceStreams();
  const [openStream, setOpenStream] = useState(null);
  // How many cards each section shows. Kept across the minute refresh, so a
  // reader halfway down Recent recordings is not snapped back to ten.
  const [shown, setShown] = useState({ live: SECTION_STEP, upcoming: SECTION_STEP, recent: SECTION_STEP });
  const showMore = (kind) =>
    setShown((current) => ({ ...current, [kind]: current[kind] + SECTION_STEP }));

  const live = useMemo(() => {
    const rows = sortByScope(resultsOf(data?.live));
    return [...rows.filter((row) => !row.continuous), ...rows.filter((row) => row.continuous)];
  }, [data]);
  // Upcoming and recent keep the API's order: soonest first, newest first.
  const upcoming = useMemo(() => resultsOf(data?.upcoming), [data]);
  const recent = useMemo(() => resultsOf(data?.recent), [data]);
  // Whether the place's city or county has no linked channel. Two signals,
  // either one enough:
  //
  //   notes             the API's documented answer -- "when a city or county
  //                     in the place has none, the notes say so" -- matched on
  //                     the scope word, so a rewording of the rest of the
  //                     sentence still counts;
  //   channels_watched  the per-scope channel counts, which omit a scope with
  //                     nothing to watch -- but only for a city or county the
  //                     API actually placed, i.e. one with a GEOID.
  //
  // The GEOID condition matters. A city-only address ("Connersville, IN")
  // comes back with `place.city` set and `place_geoid` null: the API matched
  // the name but could not place it, so it watched no city channels at all.
  // Reading that absence as "this city links no channel" said Connersville had
  // none, when it has one the API simply never looked up.
  const place = data?.place;
  const watched = data?.channels_watched || {};
  const notes = Array.isArray(data?.notes) ? data.notes.filter((note) => typeof note === 'string') : [];
  const noteMentions = (scope) =>
    notes.some((note) => /no youtube channel/i.test(note) && new RegExp('\\b' + scope + '\\b', 'i').test(note));
  const GEOID_FIELD = { city: 'place_geoid', county: 'county_geoid' };
  const noneIn = (scope) =>
    noteMentions(scope) || (Boolean(place?.[GEOID_FIELD[scope]]) && !(Number(watched[scope]) > 0));

  const warnings = [
    noneIn('city')
      ? t(
          'common:noCityChannel',
          'No YouTube channel is linked from this city\u2019s official website, so it has no streams here.'
        )
      : null,
    noneIn('county')
      ? t(
          'common:noCountyChannel',
          'No YouTube channel is linked from this county\u2019s official website, so it has no streams here.'
        )
      : null,
  ].filter(Boolean);

  const section = (title, rows, kind, emptyText) => {
    const visibleRows = rows.slice(0, shown[kind]);
    const remaining = rows.length - visibleRows.length;
    const capped = rows.length >= PLACE_STREAMS_LIMIT && remaining === 0;

    return (
      <section className="video-feed-section">
        <h4 className="video-feed-heading">
          {title}
          {rows.length > 0 && <span className="video-feed-count">{rows.length}</span>}
        </h4>
        {rows.length > 0 ? (
          <>
            {visibleRows.map((stream) => (
              <StreamCard
                key={`${kind}-${stream.video_id}`}
                stream={stream}
                kind={kind}
                showThumbnail
                onWatch={setOpenStream}
              />
            ))}
            <div className="video-feed-more">
              <LoadMore
                hasMore={remaining > 0}
                loading={false}
                onClick={() => showMore(kind)}
                label={t('common:showMoreStreams', 'Show {{count}} more', {
                  count: Math.min(SECTION_STEP, remaining),
                })}
              />
            </div>
            {capped && (
              <p className="video-feed-empty">
                {t('common:streamsCapped', 'Showing the first {{count}}.', { count: PLACE_STREAMS_LIMIT })}
              </p>
            )}
          </>
        ) : (
          <p className="video-feed-empty">{emptyText}</p>
        )}
      </section>
    );
  };

  return (
    <div className="video-feed">
      {warnings.length > 0 && (
        <ul className="video-feed-bubbles">
          {warnings.map((warning, index) => (
            <li key={index} className="video-feed-bubble">
              {warning}
            </li>
          ))}
        </ul>
      )}

      {loading && !data && (
        <div className="d-flex justify-content-center py-4">
          <Spinner animation="grow" variant="light" />
        </div>
      )}

      {data && (
        <>
          {section(
            t('common:liveNowLabel', 'Live now'),
            live,
            'live',
            t('common:noLiveStreams', 'No government here is streaming right now.')
          )}
          {section(
            t('common:streamsScheduled', 'Scheduled'),
            upcoming,
            'upcoming',
            t('common:noUpcomingStreams', 'Nothing scheduled in the next three days.')
          )}
          {section(
            t('common:streamsRecordings', 'Recent recordings'),
            recent,
            'recent',
            t('common:noRecentStreams', 'No streams ended in the last day.')
          )}
        </>
      )}

      <StreamPlayerModal stream={openStream} onHide={() => setOpenStream(null)} />

      <style>{`
        /* Show more sits centered under its section. LoadMore's button carries
           Bootstrap's me-2, whose right margin is !important and would pull
           it half a gap off center, so that margin is cleared here too. */
        .video-feed-more .feed-load-more {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }
        .video-feed-more .feed-load-more .btn {
          margin-right: 0 !important;
        }
      `}</style>
    </div>
  );
}

export default VideoFeed;